import { formatCurrency, type Currency, type ProgressBarResponse } from '@skin-seoul/service';

const formatDiscountReward = (
  tier: ProgressBarResponse['discountTiers'][number],
  currency: Currency,
): string => {
  if (tier.discountType === 'fixed_cart') {
    return `${formatCurrency(currency, tier.discountAmount)} off`;
  }

  return `${tier.percentage}% off`;
};

/**
 * Returns the authenticated-user milestone banner message keyed off the cart's
 * current progress through the discount tiers.
 *
 * Bold markers (**text**) are used to indicate semibold text — use parseMessageWithBold()
 * to render them.
 */
export function getAuthenticatedMilestoneBannerMessage(params: {
  progressBar: ProgressBarResponse | null;
  currency: Currency;
}): string | null {
  const { progressBar, currency } = params;
  if (!progressBar) {
    return null;
  }

  const discountTiers = [...progressBar.discountTiers].sort((a, b) => a.displayOrder - b.displayOrder);
  if (discountTiers.length === 0) {
    return null;
  }

  const nextUnlock = discountTiers.find((t) => !t.available);
  const highestAvailable = [...discountTiers].reverse().find((t) => t.available);

  if (!nextUnlock) {
    if (!highestAvailable) {
      return null;
    }

    // Invariant: free-shipping threshold is always lower than the lowest discount tier,
    // so all discount tiers available implies free shipping available.
    return `**${formatDiscountReward(highestAvailable, currency)}** unlocked — plus **free shipping** on top!`;
  }

  const formatted = formatCurrency(currency, nextUnlock.remaining);
  if (!highestAvailable) {
    return `Add ${formatted} more to unlock **${formatDiscountReward(nextUnlock, currency)}**.`;
  }

  return `You're getting **${formatDiscountReward(highestAvailable, currency)}** — **add ${formatted}** more for **${formatDiscountReward(nextUnlock, currency)}**!`;
}
